import { isAxiosError, isCancel } from "axios";
import {
  isCodesmithPath,
  isProofPath,
} from "@/components/sidebar/useActiveApp";
import { isStaleChunkError } from "@/hooks/useStaleChunkRecovery";
import { isBotBrowser } from "@/lib/botBrowser";
import { buildSha } from "@/lib/buildInfo";
import { codesmithSurface } from "@/lib/codesmithSurface";
import { describeRequest } from "@/lib/interactionTrace";
import { reportedRoute } from "@/lib/reportWebVital";

/** Mirrored in FrontendClientErrorsController::KINDS. */
type ClientErrorKind =
  | "render_crash"
  | "unhandled_error"
  | "unhandled_rejection"
  | "api_5xx"
  | "api_network"
  | "send_failed"
  | "ws_error"
  | "stale_chunk";
type ClientErrorSurface = "dashboard" | "codesmith";

/** The Sentry tag error boundaries set so the beacon counts their events as crashes. */
export const CLIENT_ERROR_KIND_TAG = "client_error_kind";
/** Links a Sentry event to its staging crash alert, which names the fingerprint. */
export const CLIENT_ERROR_FINGERPRINT_TAG = "client_error_fingerprint";

const REPORT_URL = `${process.env.NEXT_PUBLIC_API_URL}/api/frontend/client-errors`;
const MAX_REPORTS_PER_PAGE = 30;
const FINGERPRINT_COOLDOWN_MS = 60_000;
/** What the backend keeps; a longer non-ASCII title URL-encodes past its 4 KB body limit. */
const TITLE_MAX = 200;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const SESSION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SENTRY_EVENT_ID = /^[0-9a-f]{32}$/;
const HEX_RUN = /\b(?=[0-9a-f]*\d)[0-9a-f]{6,}\b/gi;
const NUMBER = /\d+/g;
const SHA = /^[0-9a-f]{7,40}$/i;
/** A client still running the previous deploy asks for a module the new one dropped. */
const STALE_MODULE = /module factory is not available/;

/** Pages that render [code]smith content outside the sessions routes. */
const CODESMITH_ROUTES = new Set([
  "/[org]/canvas/[uuid]",
  "/canvas-sandbox",
  "/canvas-capture",
  "/extension",
]);

/**
 * Noise Sentry's global handlers capture without it being a defect:
 * ResizeObserver loop notices, opaque cross-origin "Script error.", Playwright
 * bindings from automation, and the rejection Outlook SafeLinks' crawler
 * leaves.
 */
export const IGNORED_CLIENT_ERRORS = [
  /ResizeObserver loop/,
  /^Script error\.?$/,
  /__pw_\w+ is not exposed/,
  /Object Not Found Matching Id/,
];

let reportsSent = 0;
const lastSentAt = new Map<string, number>();

/** `[code]smith` for sessions, Proof, canvases and the extension embed; `dashboard` for every other page. */
function clientErrorSurface(route: string, search: string): ClientErrorSurface {
  const embed = new URLSearchParams(search).get("embed");
  return isCodesmithPath(route) ||
    isProofPath(route) ||
    CODESMITH_ROUTES.has(route) ||
    codesmithSurface(embed) === "extension"
    ? "codesmith"
    : "dashboard";
}

/**
 * Eight hex chars naming one failure across pages, page loads and deploys:
 * ids, hashes and counts are dropped from the title first, so
 * `Session 0194f1b2-... failed after 3 tries` and the same message for
 * another session share a fingerprint. Minified frames change every deploy,
 * so the stack is left out.
 */
function clientErrorFingerprint(parts: string[]): string {
  const normalized = parts
    .join("\n")
    .replace(UUID, "<id>")
    .replace(HEX_RUN, "<hex>")
    .replace(NUMBER, "<n>");
  let hash = 0x811c9dc5;
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

interface ClientErrorReport {
  kind: ClientErrorKind;
  title: string;
  /** Defaults to the current page's surface. */
  surface?: ClientErrorSurface;
  sentryEventId?: string;
  sessionId?: string | null;
}

/**
 * Posts one failure to the backend's client error counter and log, at most
 * once a minute per fingerprint and 30 times per page load, and returns its
 * fingerprint, also when the post is skipped. Never throws.
 * Bots and automation are skipped like they are for Sentry.
 */
function reportClientError(report: ClientErrorReport): string | undefined {
  try {
    if (typeof window === "undefined" || isBotBrowser()) return;
    const route = reportedRoute() ?? "other";
    const surface =
      report.surface ?? clientErrorSurface(route, window.location.search);
    const fingerprint = clientErrorFingerprint([report.kind, report.title]);
    const now = Date.now();
    const last = lastSentAt.get(fingerprint);
    if (
      reportsSent >= MAX_REPORTS_PER_PAGE ||
      (last !== undefined && now - last < FINGERPRINT_COOLDOWN_MS)
    ) {
      return fingerprint;
    }
    reportsSent++;
    lastSentAt.set(fingerprint, now);

    const fields: Record<string, string> = {
      surface,
      kind: report.kind,
      fingerprint,
      title: report.title.slice(0, TITLE_MAX),
      route,
    };
    const sha = buildSha();
    if (sha && SHA.test(sha)) fields.release = sha;
    if (report.sentryEventId && SENTRY_EVENT_ID.test(report.sentryEventId)) {
      fields.sentry_event_id = report.sentryEventId;
    }
    if (report.sessionId && SESSION_ID.test(report.sessionId)) {
      fields.session_id = report.sessionId;
    }

    // Form-encoded with keepalive, like postFrontendMetric: no preflight, and
    // the post survives a crash that unloads the page.
    void fetch(REPORT_URL, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      body: new URLSearchParams(fields),
    }).catch(() => {});
    return fingerprint;
  } catch {
    // Reporting never adds a failure of its own.
    return undefined;
  }
}

interface ReportableSentryEvent {
  event_id?: string;
  tags?: Record<string, unknown>;
  exception?: {
    values?: {
      type?: string;
      value?: string;
      mechanism?: { type?: string };
    }[];
  };
}

function sentryEventKind(
  kindTag: unknown,
  mechanism: string,
  title: string,
  originalException: unknown,
): ClientErrorKind | null {
  if (isStaleChunkError(originalException) || STALE_MODULE.test(title)) {
    return "stale_chunk";
  }
  if (kindTag === "render_crash") return "render_crash";
  if (mechanism === "auto.browser.global_handlers.onerror") {
    return "unhandled_error";
  }
  // Throws inside timers, rAF, XHR and event listeners; Sentry then drops the duplicate `onerror`.
  if (mechanism.startsWith("auto.browser.browserapierrors.")) {
    return "unhandled_error";
  }
  if (mechanism === "auto.browser.global_handlers.onunhandledrejection") {
    return "unhandled_rejection";
  }
  return null;
}

/**
 * Counts a Sentry event the dashboard keeps, once its filters pass: crashes
 * error boundaries tag with `client_error_kind`, and uncaught errors and
 * rejections from Sentry's global handlers. Axios errors are left to the API
 * client interceptors, and other captures (warnings, handled errors) are not
 * counted. Returns the counted event's fingerprint.
 */
export function reportSentryEvent(
  event: ReportableSentryEvent,
  originalException: unknown,
): string | undefined {
  if (isAxiosError(originalException)) return undefined;
  const exception = event.exception?.values?.at(-1);
  if (!exception) return undefined;

  const title = `${exception.type ?? "Error"}: ${exception.value ?? ""}`;
  const kind = sentryEventKind(
    event.tags?.[CLIENT_ERROR_KIND_TAG],
    exception.mechanism?.type ?? "",
    title,
    originalException,
  );
  if (kind === null) return undefined;

  const sessionId = event.tags?.session_id;
  return reportClientError({
    kind,
    title,
    sentryEventId: event.event_id,
    sessionId: typeof sessionId === "string" ? sessionId : null,
  });
}

/** Whether a failed request was cancelled or aborted by navigation rather than failing. */
export function isAbortedRequest(error: unknown): boolean {
  return (
    isCancel(error) ||
    (error instanceof Error &&
      (error.name === "AbortError" || error.message === "Request aborted"))
  );
}

/**
 * `GET /api/user/github/orgs/{org}/runs/{id} 502`: the failed request's
 * method and route template with its status or error code. Never the org,
 * query string or body.
 */
function describeFailedRequest(error: unknown): string {
  if (!isAxiosError(error) || !error.config) return String(error);
  const method = (error.config.method ?? "get").toUpperCase();
  const route = describeRequest(error.config).route.replace(
    /\/orgs\/[^/]+/,
    "/orgs/{org}",
  );
  return `${method} ${route} ${error.response?.status ?? error.code ?? "network_error"}`;
}

/**
 * For the dashboard API clients' response interceptors: counts 5xx responses
 * and requests that got no response. 4xx answers, cancellations, telemetry
 * posts and requests made while the browser is offline or the tab hidden (a
 * sleeping laptop) are not failures of ours.
 */
export function reportApiFailure(error: unknown): void {
  try {
    countApiFailure(error);
  } catch {
    // Reporting must never replace the request's own rejection.
  }
}

/** A [code]smith session's API path, e.g. `/assistant/sessions/<id>/messages/stream`. */
const SESSION_REQUEST = /\/assistant\/sessions\/([0-9a-f-]{36})(?:[/?]|$)/i;

function countApiFailure(error: unknown): void {
  if (!isAxiosError(error) || isAbortedRequest(error)) return;
  // The dashboard's own telemetry posts are not something the user waits on.
  if (
    error.config &&
    describeRequest(error.config).route.startsWith("/api/frontend/")
  ) {
    return;
  }
  const status = error.response?.status;
  if (status !== undefined && status < 500) return;
  if (status === undefined && isBrowserAway()) return;
  reportClientError({
    kind: status === undefined ? "api_network" : "api_5xx",
    title: describeFailedRequest(error),
    sessionId: SESSION_REQUEST.exec(error.config?.url ?? "")?.[1] ?? null,
  });
}

/** Offline, or the tab is hidden, e.g. a sleeping laptop. */
function isBrowserAway(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator.onLine === false || document.visibilityState === "hidden")
  );
}

/** A fetch that got no response: Chrome, Firefox and Safari wording. */
const NETWORK_FAILURE = /Failed to fetch|NetworkError|Load failed/;

function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError && NETWORK_FAILURE.test(error.message);
}

function describeFailure(error: unknown): string {
  return error instanceof Error
    ? `${error.name}: ${error.message}`
    : String(error);
}

const ASSISTANT_CHANNEL = /assistant\.session\.([0-9a-f-]{36})$/i;

/** `private-App.Models.User.4182` → `private-App.Models.User.{id}` */
const CHANNEL_ID_SEGMENT =
  /\.(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\.|$)/gi;

/**
 * For the Echo channel authorizer: counts a channel auth request that got a
 * 5xx or threw, as `ws_error`. `failure` is the response status or what the
 * request threw. A 4xx is the server refusing on purpose: no access to the
 * channel (403) or an expired session (401).
 */
export function reportChannelAuthFailure(
  channelName: string,
  failure: unknown,
): void {
  try {
    if (typeof failure === "number" ? failure < 500 : skipThrown(failure)) {
      return;
    }
    const sessionId = ASSISTANT_CHANNEL.exec(channelName)?.[1] ?? null;
    const channel = channelName.replace(CHANNEL_ID_SEGMENT, ".{id}");
    const cause =
      typeof failure === "number" ? String(failure) : describeFailure(failure);
    reportClientError({
      kind: "ws_error",
      title: `Channel auth failed for ${channel}: ${cause}`,
      surface: sessionId ? "codesmith" : undefined,
      sessionId,
    });
  } catch {
    // Pusher must still get the authorizer's callback when reporting fails.
  }
}

/** A cancelled request, or one that failed because the browser is away. */
function skipThrown(error: unknown): boolean {
  return (
    isAbortedRequest(error) || (isNetworkFailure(error) && isBrowserAway())
  );
}

/**
 * For [code]smith failures the user sees that are not failed API requests,
 * e.g. a stream replay that could not resume or an op response that did not
 * parse. Failed requests are left to the API client's interceptor, which
 * counts them as `api_5xx` or `api_network`.
 */
export function reportCodesmithFailure(
  kind: "send_failed" | "ws_error",
  what: string,
  error: unknown,
  sessionId: string | null,
): void {
  try {
    if (isAxiosError(error) || skipThrown(error)) return;
    reportClientError({
      kind,
      surface: "codesmith",
      title: `${what}: ${describeFailure(error)}`,
      sessionId,
    });
  } catch {
    // Callers like the send queue must still settle when reporting fails.
  }
}
